import { useState } from 'react';
import {
  Line,
  LineChart,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from 'recharts';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { AggregateMetricsTimePeriod } from 'src/WebApiController';

import { DashboardSectionWrapper } from '../../DashboardSectionWrapper';
import { BasicTooltip } from '../BasicTooltip';
import * as styles from '../DataInsightsChart.css';
import { SaleChartData, useDataInsights } from '../DataInsightsContext';
import { NoData } from '../NoData';
import { PingDot } from './ChartDot';
import { TargetPill } from './TargetPill';

export const DataLineChart = ({
  data,
  paddedData,
  setValue,
  valueKey,
  renderTooltipValue,
}: {
  data: SaleChartData[] | null;
  paddedData: SaleChartData[] | null;
  setValue: React.Dispatch<React.SetStateAction<number | null>>;
  valueKey: keyof SaleChartData;
  tooltipVariant?: 'default' | 'currency';
  renderTooltipValue?: (item: Record<string, unknown>) => string;
}) => {
  const { timePeriod, targetValue, uiCurrency, setIsScrolling, isLoading } =
    useDataInsights();
  const [showTooltip, setShowTooltip] = useState(true);
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: uiCurrency.code,
  });

  const latestDataPoint = data ? data[data.length - 1] : null;

  const { dateTime: latestDateTime } = latestDataPoint || {};
  const latestValue = latestDataPoint ? latestDataPoint[valueKey] : undefined;

  if (isLoading) {
    return (
      <div className={styles.loadingSpinner}>
        <PosSpinner />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return <NoData />;
  }

  return (
    <DashboardSectionWrapper>
      <ResponsiveContainer width="100%" height={450}>
        <LineChart
          data={paddedData || []}
          dataKey="date"
          onMouseLeave={() => {
            setValue(latestValue as number);
            setIsScrolling(false);
          }}
          onMouseEnter={() => {
            setIsScrolling(true);
          }}
          style={{ borderBottom: `1px solid ${vars.color.borderLight}` }}
        >
          <XAxis
            dataKey="dateTime"
            type="category"
            hide={true}
            allowDuplicatedCategory={false}
          />
          <Line
            type="monotone"
            dataKey={valueKey}
            stroke={vars.color.textBrand}
            dot={false}
            activeDot={false}
            strokeWidth={2}
            isAnimationActive={false}
          />
          <Tooltip
            isAnimationActive={false}
            cursor={
              showTooltip
                ? {
                    stroke: vars.color.borderLight,
                    strokeWidth: 1,
                  }
                : false
            }
            position={{
              y: 20,
            }}
            content={(props) => {
              const { label, payload } = props;
              if (!payload || (payload && payload.length < 1)) {
                if (setShowTooltip) {
                  setShowTooltip(false);
                }
              } else {
                setShowTooltip(true);
              }
              return (
                <BasicTooltip
                  label={label}
                  payload={payload}
                  setValue={setValue}
                  renderValue={
                    renderTooltipValue
                      ? (item) => renderTooltipValue(item.value)
                      : (item) => currencyFormatter.format(item.value)
                  }
                />
              );
            }}
          />
          {targetValue && (
            <ReferenceLine
              y={targetValue}
              label={<TargetPill />}
              strokeDasharray="4 4"
            />
          )}

          {latestDateTime &&
            typeof latestValue === 'number' &&
            timePeriod === AggregateMetricsTimePeriod.Today && (
              <ReferenceDot
                r={20}
                x={latestDateTime}
                y={latestValue}
                isFront={true}
                ifOverflow={'visible'}
                shape={<PingDot />}
              />
            )}
        </LineChart>
      </ResponsiveContainer>
    </DashboardSectionWrapper>
  );
};
